(function (root) {
  'use strict';

  function positiveInteger(value, fallback) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : fallback;
  }

  function getQuantityRules(input) {
    const min = positiveInteger(input && input.getAttribute('min'), 1);
    const rawMax = positiveInteger(input && input.getAttribute('max'), Infinity);
    const max = rawMax >= min ? rawMax : Infinity;
    const step = positiveInteger(input && input.getAttribute('step'), 1);
    const value = Math.min(max, Math.max(min, positiveInteger(input && input.value, min)));
    return { min, max, step, value };
  }

  function nextQuantity(input, direction) {
    const rules = getQuantityRules(input);
    const index = (rules.value - rules.min) / rules.step;
    const nextIndex = direction > 0 ? Math.floor(index) + 1 : Math.ceil(index) - 1;
    const upper = Number.isFinite(rules.max)
      ? rules.min + Math.floor((rules.max - rules.min) / rules.step) * rules.step : Infinity;
    return Math.max(rules.min, Math.min(upper, rules.min + nextIndex * rules.step));
  }

  function purchaseVisible(rect, viewportHeight, headerBottom, barHeight) {
    if (!rect || rect.height <= 0 || rect.width === 0) return false;
    const top = Math.max(rect.top, headerBottom, 0);
    const bottom = Math.min(rect.bottom, viewportHeight - barHeight);
    return Math.max(0, bottom - top) >= Math.min(32, rect.height * 0.65);
  }

  function buttonBusy(button) {
    return !button || button.disabled || button.getAttribute('aria-disabled') === 'true' ||
      button.getAttribute('aria-busy') === 'true' || button.classList.contains('loading');
  }

  function visibleElement(element, view) {
    if (!element.isConnected || !element.getClientRects().length) return false;
    for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
      if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      const style = view.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' ||
          style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    return true;
  }

  function blockingOverlayOpen(document, view) {
    if (document.body.classList.contains('bcw-blocking-overlay-open')) return true;
    const selector = 'cart-drawer.active, cart-drawer[open], cart-notification.active, dialog[open], ' +
      'modal-dialog[open], product-modal[open], quick-add-modal[open], ' +
      '[role="dialog"][aria-modal="true"], [data-kl-scroll-locking-modal="true"], ' +
      'details.menu-drawer-container[open], .country-selector:not([hidden]), #bcw-geo.is-open:not([hidden])';
    return Array.from(document.querySelectorAll(selector)).some((element) => visibleElement(element, view));
  }

  const exported = { getQuantityRules, nextQuantity, purchaseVisible, buttonBusy, blockingOverlayOpen };
  if (typeof module !== 'undefined' && module.exports) module.exports = exported;
  if (!root || !root.HTMLElement || !root.customElements || root.customElements.get('bcw-usa-mobile-buybar')) return;

  class MobileBuybar extends root.HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._document = this.ownerDocument;
      this._view = this._document.defaultView || root;
      this._listeners = [];
      this._frame = 0;
      this._settleTimer = 0;
      this._main = null;
      this._optionSignature = '';
      this._select = this.querySelector('[data-bcw-variant]');
      this._variantControl = this.querySelector('[data-bcw-variant-control]');
      this._quantityControl = this.querySelector('[data-bcw-quantity-control]');
      this._quantity = this.querySelector('[data-bcw-quantity]');
      this._minus = this.querySelector('[data-bcw-minus]');
      this._plus = this.querySelector('[data-bcw-plus]');
      this._button = this.querySelector('[data-bcw-submit]');
      this._label = this.querySelector('[data-bcw-submit-label]');
      this._media = this._view.matchMedia('(max-width: 1023px)');
      this._queue = this._schedule.bind(this);
      this._listen(this._minus, 'click', () => this._changeQuantity(-1));
      this._listen(this._plus, 'click', () => this._changeQuantity(1));
      this._listen(this._select, 'change', () => this._changeVariant(this._select.value));
      this._listen(this._button, 'click', () => this._submit());
      ['change', 'input', 'click', 'keydown', 'quantity:change', 'discount:applied',
        'bcw:overlay-change', 'visibilitychange', 'shopify:section:load', 'shopify:section:unload']
        .forEach((name) => this._listen(this._document, name, this._queue));
      ['toggle', 'close', 'transitionrun', 'transitionend', 'animationstart', 'animationend']
        .forEach((name) => this._listen(this._document, name, this._queue, true));
      this._listen(this._view, 'scroll', this._queue, { passive: true });
      this._listen(this._view, 'resize', this._queue, { passive: true });
      this._listen(this._view, 'pageshow', this._queue);
      this._listen(this._media, 'change', this._queue);
      if (this._view.visualViewport) {
        this._listen(this._view.visualViewport, 'resize', this._queue, { passive: true });
        this._listen(this._view.visualViewport, 'scroll', this._queue, { passive: true });
      }
      this._observer = new this._view.MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) {
          this._schedule();
          this._view.clearTimeout(this._settleTimer);
          this._settleTimer = this._view.setTimeout(this._queue, 180);
        }
      });
      this._observer.observe(this._document.body, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-modal',
          'aria-disabled', 'aria-busy', 'disabled', 'value', 'min', 'max', 'step', 'data-kl-scroll-locking-modal']
      });
      this._sync();
    }

    disconnectedCallback() {
      this._connected = false;
      if (this._frame) this._view.cancelAnimationFrame(this._frame);
      this._view.clearTimeout(this._settleTimer);
      this._frame = 0;
      this._settleTimer = 0;
      if (this._focusCleanup) this._focusCleanup();
      (this._listeners || []).forEach(([target, name, callback, options]) => target.removeEventListener(name, callback, options));
      this._listeners = [];
      if (this._observer) this._observer.disconnect();
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      if (this._document) {
        this._document.documentElement.removeAttribute('data-bcw-usa-mobile-buybar-ready');
        this._document.documentElement.removeAttribute('data-bcw-usa-overlay-open');
      }
    }

    _listen(target, name, callback, options) {
      if (!target) return;
      target.addEventListener(name, callback, options);
      this._listeners.push([target, name, callback, options]);
    }

    _schedule() {
      if (!this._connected || this._frame) return;
      this._frame = this._view.requestAnimationFrame(() => {
        this._frame = 0;
        if (this._connected) this._sync();
      });
    }

    _bindMain() {
      const handle = this.dataset.productHandle;
      const info = Array.from(this._document.querySelectorAll('main product-info[data-url], #MainContent product-info[data-url]'))
        .find((element) => {
          const match = (element.getAttribute('data-url') || '').match(/\/products\/([^/?#]+)/);
          return match && match[1] === handle && !element.closest('quick-add-modal, product-recommendations');
        });
      const form = info && info.querySelector('product-form form[data-type="add-to-cart-form"], product-form form[action*="/cart/add"]');
      const button = form && form.querySelector('button[type="submit"][name="add"]');
      const variant = form && form.querySelector('input.product-variant-id[name="id"], input[type="hidden"][name="id"], select[name="id"]');
      if (!info || !form || !button || !variant) {
        this._main = null;
        return null;
      }
      const quantity = Array.from(info.querySelectorAll('input[name="quantity"]'))
        .find((input) => input.form === form || input.getAttribute('form') === form.id || form.contains(input));
      this._main = { info, form, button, variant, quantity };
      return this._main;
    }

    _getVariants() {
      const main = this._main;
      if (!main) return [];
      const cards = Array.from(main.info.querySelectorAll('label[data-variant-id]'));
      const variants = cards.map((card) => {
        const radio = card.querySelector('input[type="radio"][name="id"]');
        if (!radio || (radio.form !== main.form && radio.getAttribute('form') !== main.form.id)) return null;
        const title = card.querySelector('span');
        const titleGroup = title && title.parentElement;
        const label = title ? (titleGroup && titleGroup !== card ? titleGroup.textContent : title.textContent) :
          (radio.getAttribute('aria-label') || radio.value);
        return { value: String(radio.value), label: label.replace(/\s+/g, ' ').trim(),
          disabled: radio.disabled || card.getAttribute('data-variant-available') === 'false', card, radio };
      }).filter(Boolean);
      if (variants.length) return variants;
      const select = main.form.querySelector('select[name="id"]');
      return select ? Array.from(select.options).map((option) => ({
        value: String(option.value), label: option.textContent.trim(), disabled: option.disabled, select
      })) : [];
    }

    _sync() {
      this._updateOverlayFlag();
      const main = this._bindMain();
      if (!main) {
        this._setVisible(false);
        this._document.documentElement.removeAttribute('data-bcw-usa-mobile-buybar-ready');
        return;
      }
      this._variants = this._getVariants();
      this._chooseMode = this.dataset.complexOptions === 'true' || this._variants.length > 8 ||
        (this.dataset.hasVariants === 'true' && this._variants.length === 0);
      const signature = JSON.stringify(this._variants.map(({ value, label, disabled }) => [value, label, disabled]));
      if (signature !== this._optionSignature) {
        this._select.replaceChildren();
        this._variants.forEach((variant) => {
          const option = this._document.createElement('option');
          option.value = variant.value;
          option.textContent = variant.label;
          option.disabled = variant.disabled;
          this._select.appendChild(option);
        });
        this._optionSignature = signature;
      }
      this._select.value = main.variant.value;
      this._variantControl.hidden = this._chooseMode || this._variants.length <= 1;
      this._quantityControl.hidden = this._chooseMode || !main.quantity;
      const busy = buttonBusy(main.button);
      const pending = main.button.classList.contains('loading') ||
        main.button.getAttribute('aria-busy') === 'true' || main.button.getAttribute('aria-disabled') === 'true';
      this._select.disabled = pending;
      const rules = getQuantityRules(main.quantity);
      this._quantity.value = rules.value;
      this._quantity.min = rules.min;
      this._quantity.step = rules.step;
      this._minus.disabled = busy || this._chooseMode || !main.quantity || nextQuantity(main.quantity, -1) === rules.value;
      this._plus.disabled = busy || this._chooseMode || !main.quantity || nextQuantity(main.quantity, 1) === rules.value;
      const selectedKnown = !this._variants.length || this._variants.some((variant) => variant.value === String(main.variant.value) && !variant.disabled);
      this._button.disabled = !this._chooseMode && (busy || !main.variant.value || main.variant.disabled || !selectedKnown);
      this._button.setAttribute('aria-busy', String(!this._chooseMode &&
        (main.button.classList.contains('loading') || main.button.getAttribute('aria-busy') === 'true')));
      const sourceLabel = main.button.querySelector('span');
      this._label.textContent = this._chooseMode ? this.dataset.chooseLabel :
        ((sourceLabel && sourceLabel.textContent.trim()) || (busy ? this.dataset.unavailableLabel : this.dataset.addLabel));
      this._document.documentElement.setAttribute('data-bcw-usa-mobile-buybar-ready', 'true');
      this._position();
    }

    _position() {
      const main = this._main;
      const overlayOpen = this._updateOverlayFlag();
      if (!main || !this._media.matches || overlayOpen) {
        this._setVisible(false);
        return;
      }
      const rect = main.button.getBoundingClientRect();
      if (!rect.height || !rect.width) {
        this._setVisible(false);
        return;
      }
      const header = this._document.querySelector('.header-group-sticky');
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const barHeight = this.offsetHeight || (this._variantControl.hidden ? 64 : 116);
      const viewport = this._view.visualViewport;
      const keyboardOpen = viewport && this._view.innerHeight - viewport.height > 150 &&
        this._document.activeElement && this._document.activeElement.matches('input:not([readonly]), textarea, [contenteditable="true"]');
      this._setVisible(!keyboardOpen && !purchaseVisible(rect, this._view.innerHeight, headerBottom, barHeight));
    }

    _updateOverlayFlag() {
      const open = blockingOverlayOpen(this._document, this._view);
      const element = this._document.documentElement;
      if (open) element.setAttribute('data-bcw-usa-overlay-open', 'true');
      else element.removeAttribute('data-bcw-usa-overlay-open');
      return open;
    }

    _setVisible(visible) {
      this.hidden = !visible;
      this.setAttribute('aria-hidden', String(!visible));
    }

    _changeQuantity(direction) {
      const main = this._bindMain();
      if (!main || !main.quantity || this._chooseMode || buttonBusy(main.button)) return;
      const next = nextQuantity(main.quantity, direction);
      if (String(next) !== main.quantity.value) {
        main.quantity.value = String(next);
        main.quantity.dispatchEvent(new this._view.Event('input', { bubbles: true }));
        main.quantity.dispatchEvent(new this._view.Event('change', { bubbles: true }));
      }
      this._sync();
    }

    _changeVariant(value) {
      if (!this._bindMain() || this._chooseMode) return;
      const button = this._main.button;
      if (button.classList.contains('loading') || button.getAttribute('aria-busy') === 'true' || button.getAttribute('aria-disabled') === 'true') return;
      const source = this._getVariants().find((variant) => variant.value === String(value) && !variant.disabled);
      if (!source) {
        this._sync();
        return;
      }
      if (source.card) source.card.click();
      else if (source.select) {
        source.select.value = source.value;
        source.select.dispatchEvent(new this._view.Event('change', { bubbles: true }));
      }
      this._sync();
    }

    _chooseOptions() {
      const main = this._main;
      if (!main) return;
      const nativePicker = main.info.querySelector('variant-selects, select[name="id"]:not([disabled])');
      const availableVariant = nativePicker ? null : this._getVariants().find((variant) => variant.card && !variant.disabled);
      const target = nativePicker || (availableVariant && availableVariant.card) || main.info;
      const reduce = this._view.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      if (this._focusCleanup) this._focusCleanup();
      const selector = 'select:not([disabled]), button:not([disabled]), input:not([disabled]):not([type="hidden"])';
      const focus = target.matches(selector) ? target : target.querySelector(selector);
      if (focus) {
        focus.focus({ preventScroll: true });
        if (this._document.activeElement === focus) return;
      }
      const previousTabindex = target.getAttribute('tabindex');
      if (previousTabindex === null) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (previousTabindex === null) {
        const cleanup = () => {
          if (target.getAttribute('tabindex') === '-1') target.removeAttribute('tabindex');
          target.removeEventListener('blur', cleanup);
          if (this._focusCleanup === cleanup) this._focusCleanup = null;
        };
        this._focusCleanup = cleanup;
        target.addEventListener('blur', cleanup, { once: true });
      }
    }

    _submit() {
      if (this.hidden || blockingOverlayOpen(this._document, this._view)) return;
      this._sync();
      const main = this._main;
      if (!main || this.hidden) return;
      if (this._chooseMode) {
        this._chooseOptions();
        return;
      }
      if (this._button.disabled || buttonBusy(main.button) || !main.variant.value || main.variant.disabled) return;
      main.button.click();
      this._sync();
    }
  }

  exported.MobileBuybar = MobileBuybar;
  root.customElements.define('bcw-usa-mobile-buybar', MobileBuybar);
})(typeof window !== 'undefined' ? window : globalThis);
